import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { 
  ArrowRight, 
  BookOpen, 
  CheckCircle2, 
  Sparkles, 
  Shield, 
  ChevronRight,
  ExternalLink,
  Quote,
  Star,
  Globe2,
  Languages,
  Building2,
  Library,
  BookMarked
} from 'lucide-react';

export const HomePage: React.FC = () => {
  const { data: publicationsRes } = useQuery({
    queryKey: ['public-publications', { featured: 'true' }],
    queryFn: () => api.get('/public/publications?featured=true').then((res) => res.data.data),
  });

  const { data: testimonialsRes } = useQuery({
    queryKey: ['public-testimonials'],
    queryFn: () => api.get('/public/testimonials').then((res) => res.data.data),
  });

  const publications = publicationsRes || [];
  const testimonials = testimonialsRes || [];

  return (
    <div className="space-y-16 pb-20">
      
      {/* 1. CORPORATE PUBLISHING HERO - IMPRESSIVE, CONTEXT-RICH & AUTHORITATIVE */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#070A12] via-[#0C1426] to-[#080C16] text-white pt-12 sm:pt-20 pb-16 sm:pb-20 border-b border-slate-800/80 font-sans">
        {/* Ambient atmospheric lighting */}
        <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(#C29F78_1px,transparent_1px)] [background-size:28px_28px]"></div>
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[650px] h-[350px] bg-amber-500/10 rounded-full blur-[140px] pointer-events-none"></div>
        <div className="absolute -top-24 right-10 w-[400px] h-[400px] bg-brand-500/10 rounded-full blur-[120px] pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            
            {/* Left Column: Authoritative Editorial Storytelling & Context */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              
              {/* Official Corporate Badge */}
              <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[11px] font-mono tracking-wider uppercase font-semibold backdrop-blur-md">
                <Building2 className="w-3.5 h-3.5 text-amber-400" />
                <span>FOR THE TRUTH PRESS PRIVATE LIMITED</span>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                <span className="text-[10px] text-slate-300 font-normal hidden sm:inline">INCORPORATED PRESS</span>
              </div>

              {/* Master Headline */}
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-serif font-black tracking-tight leading-[1.12] text-white">
                Publishing Literature of{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-brand-300 to-amber-400">
                  Eternal Weight
                </span>{' '}
                &amp; Enduring Truth.
              </h1>

              {/* Sub-headline: Rich Publishing Context */}
              <p className="text-sm sm:text-base text-slate-300 max-w-xl mx-auto lg:mx-0 leading-relaxed font-normal">
                India’s premier theological press and rights custodian, dedicated to publishing classical Christian scholarship, puritan treatises, and faithful vernacular editions. Crafted with archival-grade typography, smyth-sewn binding, and statutory Government of India ISBN allotment.
              </p>

              {/* Publishing Context Feature Matrix */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1 max-w-xl mx-auto lg:mx-0 text-left">
                <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-900/70 border border-slate-800 text-xs text-slate-300 backdrop-blur-xs">
                  <CheckCircle2 className="w-4 h-4 text-amber-400 shrink-0" />
                  <span className="font-medium">Govt. of India ISBN (<span className="text-amber-300 font-mono">isbn.gov.in</span>)</span>
                </div>
                <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-900/70 border border-slate-800 text-xs text-slate-300 backdrop-blur-xs">
                  <Languages className="w-4 h-4 text-brand-400 shrink-0" />
                  <span className="font-medium">10+ Regional Indian Languages</span>
                </div>
                <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-900/70 border border-slate-800 text-xs text-slate-300 backdrop-blur-xs">
                  <BookMarked className="w-4 h-4 text-amber-400 shrink-0" />
                  <span className="font-medium">Archival Smyth-Sewn Production</span>
                </div>
                <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-900/70 border border-slate-800 text-xs text-slate-300 backdrop-blur-xs">
                  <Globe2 className="w-4 h-4 text-brand-400 shrink-0" />
                  <span className="font-medium">Pan-India Seminary &amp; Church Reach</span>
                </div>
              </div>

              {/* Clean Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3.5 pt-2">
                <Link
                  to="/publications"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-brand-500 hover:from-amber-400 hover:to-brand-400 text-slate-950 font-bold text-xs uppercase tracking-wider shadow-lg shadow-amber-500/20 transition-all hover:scale-[1.01]"
                >
                  <BookOpen className="w-4 h-4" />
                  <span>Explore Production Archive (432 Titles)</span>
                  <ArrowRight className="w-4 h-4 ml-0.5" />
                </Link>
                
                <Link
                  to="/contact"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900/90 hover:bg-slate-800 text-slate-200 hover:text-white font-semibold text-xs border border-slate-700 hover:border-slate-600 transition-all"
                >
                  <Building2 className="w-4 h-4 text-amber-400" />
                  <span>Rights &amp; Translation Enquiries</span>
                </Link>
              </div>

              {/* Quick Trust Anchor & Bookstore Portal */}
              <div className="pt-1 flex items-center justify-center lg:justify-start gap-4 text-xs text-slate-400">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Official Imprint: For The Truth Publishing</span>
                </span>
                <span className="text-slate-700">&bull;</span>
                <a 
                  href="https://forthetruth.in/shop" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="hover:text-amber-300 transition-colors inline-flex items-center gap-1.5 font-semibold text-slate-300"
                >
                  <span>Bookstore Retail Portal</span>
                  <ExternalLink className="w-3 h-3 text-amber-400" />
                </a>
              </div>

            </div>

            {/* Right Column: High-End Layered 3D Book & Publishing Showcase */}
            <div className="lg:col-span-5 relative">
              <div className="relative mx-auto max-w-sm sm:max-w-md lg:max-w-none">
                
                {/* Visual Glow behind Book */}
                <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/20 via-brand-500/20 to-amber-700/20 rounded-3xl blur-xl opacity-75"></div>

                <div className="relative p-6 sm:p-7 rounded-3xl bg-gradient-to-b from-slate-900/95 via-slate-950/95 to-slate-900/95 border border-slate-800/90 shadow-2xl space-y-5 backdrop-blur-xl">
                  
                  {/* Status Bar */}
                  <div className="flex items-center justify-between border-b border-slate-800/80 pb-3.5">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></div>
                      <span className="text-[11px] font-mono uppercase tracking-wider text-slate-300 font-bold">
                        Featured Production
                      </span>
                    </div>
                    <span className="text-[10px] font-mono text-amber-300 font-bold bg-amber-950/60 px-2.5 py-1 rounded-full border border-amber-700/40">
                      Verified ISBN Allotment
                    </span>
                  </div>

                  {/* Physical Book Showcase */}
                  <div className="flex gap-5 items-center">
                    {/* Book Mockup with Hardcover Depth */}
                    <div className="relative shrink-0 group">
                      <div className="w-28 sm:w-32 aspect-[3/4] rounded-lg overflow-hidden shadow-2xl shadow-black/80 border border-amber-900/30 bg-slate-900 p-1.5 relative transition-transform duration-300 group-hover:scale-105">
                        {/* Book Spine Simulation Accent */}
                        <div className="absolute left-0 top-0 bottom-0 w-2.5 bg-gradient-to-r from-black/40 via-white/10 to-transparent z-10 pointer-events-none"></div>
                        <img
                          src="https://forthetruth.in/wp-content/uploads/2022/10/truth-for-life-front-1.jpg"
                          alt="Truth for Life: Volume 2 by Alistair Begg"
                          className="w-full h-full object-contain drop-shadow-md rounded"
                        />
                      </div>
                      <div className="absolute -bottom-2 -right-2 bg-brand-950 text-amber-300 text-[9px] font-mono font-bold px-2 py-0.5 rounded border border-amber-500/40 shadow-sm">
                        Hardcover
                      </div>
                    </div>

                    {/* Book Metadata & Editorial Context */}
                    <div className="space-y-1.5 overflow-hidden">
                      <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase font-bold text-amber-400 tracking-wider">
                        <Sparkles className="w-3 h-3 text-amber-400" />
                        <span>Daily Devotional &bull; Vol. 2</span>
                      </div>
                      <h3 className="font-serif font-bold text-lg text-white tracking-tight leading-snug">
                        Truth for Life: Volume 2
                      </h3>
                      <p className="text-xs text-slate-300 font-medium">
                        By Alistair Begg
                      </p>
                      <p className="text-[11px] text-slate-400 leading-relaxed line-clamp-2">
                        365 gospel-centered reflections anchored in God&apos;s Word, crafted in Smyth-sewn archival binding.
                      </p>
                      
                      <div className="pt-1 flex items-center gap-1.5 text-[10px] text-emerald-400 font-mono font-medium">
                        <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                        <span>Available in Bookstore</span>
                      </div>
                    </div>
                  </div>

                  {/* Endorsement Preview Quote */}
                  <div className="p-3 rounded-xl bg-slate-950/80 border border-slate-800/80 text-[11px] text-slate-300 italic font-serif leading-relaxed">
                    &ldquo;Each day&rsquo;s reflection is a gem... I cannot imagine it being read without benefit!&rdquo;
                    <span className="block not-italic font-sans font-bold text-[10px] text-amber-400 mt-1">
                      — Tim Challies, Author of Seasons of Sorrow
                    </span>
                  </div>

                  {/* Direct Link Action Footer */}
                  <div className="pt-1 flex items-center justify-between gap-3 text-xs border-t border-slate-800/60">
                    <Link
                      to="/publications/truth-for-life-volume-2"
                      className="text-slate-300 hover:text-white font-medium inline-flex items-center gap-1"
                    >
                      <span>Catalog Details</span>
                      <ChevronRight className="w-3.5 h-3.5 text-amber-400" />
                    </Link>
                    <a
                      href="https://forthetruth.in/product/truth-for-life-volume-2/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3.5 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500 text-amber-300 hover:text-slate-950 font-bold border border-amber-500/40 transition-all inline-flex items-center gap-1.5"
                    >
                      <span>Store Page</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>

                </div>

              </div>
            </div>

          </div>
        </div>

        {/* 4-Metric Corporate Bar (Authoritative Publishing Benchmarks) */}
        <div className="mt-14 pt-8 border-t border-slate-800/80 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="space-y-1">
              <div className="text-2xl sm:text-4xl font-serif font-black text-white tracking-tight">432+</div>
              <div className="text-xs text-slate-400 font-medium">Published &amp; Stocked Titles</div>
            </div>
            <div className="space-y-1">
              <div className="text-2xl sm:text-4xl font-serif font-black text-amber-400 tracking-tight">290+</div>
              <div className="text-xs text-slate-400 font-medium">Theological Authors Cataloged</div>
            </div>
            <div className="space-y-1">
              <div className="text-2xl sm:text-4xl font-serif font-black text-white tracking-tight">10+</div>
              <div className="text-xs text-slate-400 font-medium">Vernacular Indian Languages</div>
            </div>
            <div className="space-y-1">
              <div className="text-2xl sm:text-4xl font-serif font-black text-amber-400 tracking-tight">100%</div>
              <div className="text-xs text-slate-400 font-medium">Archival Smyth-Sewn Standards</div>
            </div>
          </div>
        </div>

      </section>

      {/* 3. SPOTLIGHT ENDORSEMENT: TIM CHALLIES & CRITICAL ACCLAIM */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 text-white border border-slate-800 shadow-xl p-8 sm:p-12">
          
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-80 h-80 bg-brand-500/10 rounded-full blur-3xl pointer-events-none"></div>

          <div className="relative space-y-6">
            
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full bg-brand-950 border border-brand-500/40 text-brand-300 text-[10px] font-mono uppercase tracking-wider font-bold">
                Critical Acclaim &amp; Endorsements
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              
              {/* Left: The Quote */}
              <div className="lg:col-span-8 space-y-5">
                <Quote className="w-10 h-10 text-brand-400/40" />

                <blockquote className="text-lg sm:text-2xl font-serif italic text-slate-100 leading-relaxed">
                  &ldquo;If you are looking for wise and perceptive guidance to help you see and experience the deep impact of God’s words in Scripture, you will love this devotional. Each day’s reflection is a gem, focusing the light of the chosen Scripture text onto our thinking, our affections and our behaviour. I cannot imagine it being read without benefit!&rdquo;
                </blockquote>

                <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-t border-slate-800/80">
                  <div>
                    <h4 className="font-extrabold text-base text-white tracking-tight">
                      Tim Challies
                    </h4>
                    <p className="text-xs text-brand-300 font-medium">
                      Blogger; author, <em>Seasons of Sorrow</em>
                    </p>
                  </div>
                  
                  <div className="text-xs text-slate-400 font-mono">
                    Endorsement for: <strong className="text-slate-200">Truth for Life: Volume 2</strong> (Alistair Begg)
                  </div>
                </div>
              </div>

              {/* Right: Accompanying Book Thumbnail Card */}
              <div className="lg:col-span-4">
                <div className="p-4 rounded-2xl bg-slate-950/80 border border-slate-800 shadow-md flex items-center gap-4">
                  <div className="w-20 aspect-[3/4] rounded-lg overflow-hidden bg-slate-900 border border-slate-700 shrink-0 p-1 flex items-center justify-center">
                    <img
                      src="https://forthetruth.in/wp-content/uploads/2022/10/truth-for-life-front-1.jpg"
                      alt="Truth for Life Volume 2"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                  <div className="space-y-1 overflow-hidden">
                    <span className="text-[10px] font-mono text-brand-400 uppercase font-semibold block">
                      Featured Devotional
                    </span>
                    <h5 className="font-serif font-bold text-sm text-white truncate">
                      Truth for Life: Vol 2
                    </h5>
                    <p className="text-xs text-slate-400">
                      By Alistair Begg
                    </p>
                    <a
                      href="https://forthetruth.in/product/truth-for-life-volume-2/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-brand-400 hover:text-brand-300 font-bold pt-1"
                    >
                      <span>View in Bookstore</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>

            </div>

          </div>

        </div>
      </section>

      {/* 4. CORPORATE PUBLISHING OPERATIONS (REPLACES DENSE TEXT WALL) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="text-center space-y-2 max-w-2xl mx-auto">
          <span className="text-xs font-mono uppercase tracking-widest text-brand-600 font-bold">
            Corporate Architecture
          </span>
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-slate-900">
            Publishing Infrastructure &amp; Capabilities
          </h2>
          <p className="text-slate-600 text-xs sm:text-sm">
            Four specialized divisions powering distribution, translation rights, and academic publishing across India.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          
          {/* Pillar 1 */}
          <div className="p-6 rounded-2xl bg-white border border-slate-200/90 shadow-2xs hover:border-brand-300 hover:shadow-xs transition-all space-y-3">
            <div className="w-10 h-10 rounded-xl bg-brand-50 border border-brand-200 flex items-center justify-center text-brand-700">
              <BookMarked className="w-5 h-5" />
            </div>
            <h3 className="font-serif font-bold text-base text-slate-900">
              Pan-India Distribution
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Nationwide retail and wholesale logistics supplying bookstores, churches, academic seminaries, and readers across India with over 430 titles.
            </p>
            <div className="pt-2">
              <Link to="/publications" className="text-xs font-bold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1">
                <span>Browse Catalog</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {/* Pillar 2 */}
          <div className="p-6 rounded-2xl bg-white border border-slate-200/90 shadow-2xs hover:border-brand-300 hover:shadow-xs transition-all space-y-3">
            <div className="w-10 h-10 rounded-xl bg-brand-50 border border-brand-200 flex items-center justify-center text-brand-700">
              <Languages className="w-5 h-5" />
            </div>
            <h3 className="font-serif font-bold text-base text-slate-900">
              Vernacular Translations
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Licensed translation and publication of foundational Christian works into Hindi, Tamil, Telugu, Malayalam, Bengali, and other major Indian languages.
            </p>
            <div className="pt-2">
              <Link to="/services" className="text-xs font-bold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1">
                <span>Translation Pipeline</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {/* Pillar 3 */}
          <div className="p-6 rounded-2xl bg-white border border-slate-200/90 shadow-2xs hover:border-brand-300 hover:shadow-xs transition-all space-y-3">
            <div className="w-10 h-10 rounded-xl bg-brand-50 border border-brand-200 flex items-center justify-center text-brand-700">
              <Globe2 className="w-5 h-5" />
            </div>
            <h3 className="font-serif font-bold text-base text-slate-900">
              Global Imprint Rights
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Strategic rights licensing agreements with leading international reformational publishers to produce accessible, high-quality Indian editions.
            </p>
            <div className="pt-2">
              <Link to="/contact" className="text-xs font-bold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1">
                <span>Rights Enquiries</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {/* Pillar 4 */}
          <div className="p-6 rounded-2xl bg-white border border-slate-200/90 shadow-2xs hover:border-brand-300 hover:shadow-xs transition-all space-y-3">
            <div className="w-10 h-10 rounded-xl bg-brand-50 border border-brand-200 flex items-center justify-center text-brand-700">
              <Shield className="w-5 h-5" />
            </div>
            <h3 className="font-serif font-bold text-base text-slate-900">
              Academic &amp; Editorial Press
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Chicago Manual of Style editorial standards, doctrinal fidelity, classical interior typography, and durable archival-quality book production.
            </p>
            <div className="pt-2">
              <Link to="/our-process" className="text-xs font-bold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1">
                <span>Publishing Process</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* 5. CURATED PUBLICATIONS FROM STORE CATALOG */}
      <section className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div className="space-y-1 max-w-xl">
              <span className="text-xs font-mono uppercase tracking-widest text-brand-400 font-bold">
                Selected Published Works
              </span>
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-slate-100">
                Titles of Theological &amp; Pastoral Depth
              </h2>
            </div>

            <Link
              to="/publications"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-400 hover:text-brand-300 shrink-0 uppercase tracking-wider"
            >
              <span>View All 432 Publications</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {publications.slice(0, 4).map((book: any) => (
              <div
                key={book.id}
                className="group flex flex-col justify-between bg-slate-950 rounded-2xl overflow-hidden border border-slate-800 hover:border-brand-500/50 shadow-lg transition-all"
              >
                <div className="aspect-[3/4] bg-slate-950 overflow-hidden relative flex items-center justify-center p-4 border-b border-slate-800/80">
                  <img
                    src={book.coverImage}
                    alt={book.title}
                    className="max-h-full max-w-full object-contain drop-shadow-md group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-2.5 left-2.5 bg-slate-900/90 text-brand-300 text-[9px] font-mono px-2 py-0.5 rounded uppercase font-bold border border-brand-500/30">
                    {book.format}
                  </div>
                </div>

                <div className="p-4 space-y-3 flex-1 flex flex-col justify-between">
                  <div className="space-y-1">
                    <span className="text-[10px] text-brand-400 font-mono font-medium block truncate">
                      {book.category}
                    </span>
                    <h3 className="font-serif text-sm font-bold text-white group-hover:text-brand-300 transition-colors line-clamp-1">
                      {book.title}
                    </h3>
                    <p className="text-xs text-slate-400 truncate">
                      By {book.author}
                    </p>
                  </div>

                  <div className="pt-3 border-t border-slate-800/80 flex items-center justify-between text-xs">
                    <Link
                      to={`/publications/${book.slug}`}
                      className="text-white hover:text-brand-400 font-medium text-xs"
                    >
                      Details &rarr;
                    </Link>
                    {book.bookstoreUrl && (
                      <a
                        href={book.bookstoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-brand-400 hover:text-brand-300 font-semibold text-xs"
                      >
                        <span>Bookstore</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 6. SUPPORTING CRITICAL ACCLAIM & ACADEMIC VOICES */}
      {testimonials.length > 1 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <div className="text-center space-y-1 max-w-xl mx-auto">
            <span className="text-xs font-mono uppercase tracking-widest text-brand-600 font-bold">
              Scholarly Voices
            </span>
            <h2 className="text-xl sm:text-2xl font-serif font-bold text-slate-900">
              Institutional Commendation
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.slice(1, 3).map((t: any) => (
              <div
                key={t.id}
                className="p-6 rounded-2xl bg-white border border-slate-200/90 shadow-2xs space-y-4 flex flex-col justify-between"
              >
                <p className="font-serif italic text-slate-700 text-sm sm:text-base leading-relaxed">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="flex items-center gap-3 pt-3 border-t border-slate-100">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-200 border border-slate-200 shrink-0">
                    <img src={t.avatarUrl} alt={t.clientName} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <h4 className="font-bold text-xs text-slate-900">{t.clientName}</h4>
                    <p className="text-[11px] text-slate-500">{t.authorTitle} &bull; <em>{t.bookTitle}</em></p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 7. HIGH IMPACT CORPORATE & RIGHTS CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-slate-950 text-white p-8 sm:p-12 border border-slate-800 text-center space-y-6">
          <div className="max-w-2xl mx-auto space-y-3">
            <span className="text-[11px] font-mono uppercase tracking-widest text-brand-400 font-bold">
              Institutional &amp; Publishing Inquiries
            </span>
            <h2 className="text-2xl sm:text-4xl font-serif font-bold text-slate-100">
              Partner With FOR THE TRUTH PRESS PRIVATE LIMITED
            </h2>
            <p className="text-slate-300 text-xs sm:text-sm font-normal max-w-lg mx-auto leading-relaxed">
              Whether you are an international rights holder seeking Indian distribution, an academic institution ordering in bulk, or an author submitting a scholarly manuscript.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-1">
            <Link
              to="/submit-enquiry"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-slate-950 font-bold text-xs uppercase tracking-wider shadow-lg shadow-brand-500/20 transition-all hover:scale-[1.01]"
            >
              <span>Submit Publishing Proposal (RFQ)</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900 hover:bg-slate-850 text-white font-semibold text-xs border border-slate-700 transition-all"
            >
              <span>Corporate &amp; Rights Office</span>
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};
